<template>
  <div id="solar-admin-page">
    <vx-card title="Stations" class="mb-base">
      <div v-if="loading">Loading…</div>
      <div v-else-if="!stations.length" class="text-warning">
        No stations yet. The collector populates this on first cycle (~1 minute after server boot).
      </div>
      <table v-else class="w-full">
        <thead>
          <tr class="text-left">
            <th class="py-2">Name</th>
            <th class="py-2">Tariff €/kWh</th>
            <th class="py-2">Country</th>
            <th class="py-2">Score</th>
            <th class="py-2">Active</th>
            <th class="py-2"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="s in stations" :key="s.id" class="border-t">
            <td class="py-2">
              {{ s.name }}
              <span class="text-grey ml-1" style="font-size: 11px;" v-if="s.installed_capacity_kw">
                · {{ s.installed_capacity_kw }} kWp
              </span>
            </td>
            <td class="py-2">
              <input v-model.number="s._price" type="number" step="0.001" min="0" class="vs-input--input"
                style="width: 90px; padding: 4px 6px;" placeholder="0.30" />
            </td>
            <td class="py-2">
              <input v-model="s._country" type="text" maxlength="2" class="vs-input--input"
                style="width: 60px; padding: 4px 6px;" placeholder="BE" />
            </td>
            <td class="py-2">
              <span v-if="scoreFor(s.id) !== null" class="solar-score-pill" :class="scoreClassFor(s.id)">
                {{ scoreFor(s.id) }}
              </span>
              <span v-else class="text-grey">—</span>
            </td>
            <td class="py-2">
              <input v-model="s._active" type="checkbox" />
            </td>
            <td class="py-2 text-right">
              <vs-button size="small" color="primary" @click="saveStation(s)" :disabled="!isDirty(s)">
                Save
              </vs-button>
            </td>
          </tr>
        </tbody>
      </table>
    </vx-card>

    <vx-card class="mb-base">
      <h4 class="mb-3">
        <feather-icon icon="AlertTriangleIcon" svgClasses="h-5 w-5 text-danger" class="align-middle mr-1" />
        Active alerts
        <span class="text-sm text-grey ml-2" v-if="alerts.length > 0">
          ({{ criticalCount }} critical, {{ warningCount }} warning)
        </span>
      </h4>
      <div v-if="loadingAlerts" class="text-grey">Loading…</div>
      <div v-else-if="alerts.length === 0" class="text-grey">
        No active alerts. All inverters look healthy 🎉
      </div>
      <table v-else class="w-full text-sm solar-alerts-table">
        <thead>
          <tr class="text-left">
            <th class="py-1 pr-2">Severity</th>
            <th class="py-1 pr-2">Rule</th>
            <th class="py-1 pr-2">Station / Inverter</th>
            <th class="py-1 pr-2">Open since</th>
            <th class="py-1 text-right">Est. loss</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="a in alerts" :key="a.id" class="border-t">
            <td class="py-1 pr-2">
              <span class="alert-sev-pill" :class="'sev-' + a.severity">{{ a.severity }}</span>
            </td>
            <td class="py-1 pr-2">{{ ruleLabel(a.ruleCode) }}</td>
            <td class="py-1 pr-2">
              {{ a.stationName || ('Station ' + a.stationId) }}
              <span v-if="a.inverterSn" class="text-grey ml-1" style="font-size: 11px;">/ {{ a.inverterSn }}</span>
            </td>
            <td class="py-1 pr-2">{{ formatRelative(a.openedAt) }}</td>
            <td class="py-1 text-right">
              <span v-if="a.estimatedLossEur != null">
                {{ Number(a.estimatedLossEur).toFixed(2) }} €
                <span class="text-grey" style="font-size: 11px;">({{ Number(a.estimatedLossKwh || 0).toFixed(1) }} kWh)</span>
              </span>
              <span v-else class="text-grey">–</span>
            </td>
          </tr>
        </tbody>
      </table>
    </vx-card>
  </div>
</template>

<script>
import axios from "@/axios.js";

export default {
  name: "solar-admin",
  data() {
    return {
      loading: true,
      loadingAlerts: true,
      stations: [],
      scoresByStation: {},
      alerts: [],
      criticalCount: 0,
      warningCount: 0,
    };
  },
  methods: {
    async loadAll() {
      this.loading = true;
      try {
        const [scoresRes] = await Promise.all([
          axios.get("/solar/scores"),
        ]);
        const items = (scoresRes.data && scoresRes.data.items) || [];
        const scoreMap = {};
        items.forEach((it) => {
          scoreMap[it.stationId] = { score: it.score, level: it.level };
        });
        this.scoresByStation = scoreMap;

        // Walk station-admin endpoint per station id we know about (collected from scores).
        const stationDetails = await Promise.all(
          items.map((it) =>
            axios
              .get(`/solar/admin/stations/${it.stationId}`)
              .then((r) => r.data)
              .catch(() => null)
          )
        );
        const stations = [];
        for (const s of stationDetails) {
          if (!s) continue;
          stations.push({
            ...s,
            _price: s.electric_price_eur_per_kwh,
            _country: s.country_code || "",
            _active: s.is_active,
          });
        }
        this.stations = stations;
      } catch (e) {
        this.$vs.notify({ title: "Solar admin", text: e.message || "Load failed", color: "danger" });
      } finally {
        this.loading = false;
      }
    },
    async loadAlerts() {
      this.loadingAlerts = true;
      try {
        const res = await axios.get("/solar/alerts/active");
        this.alerts = (res.data && res.data.items) || [];
        this.criticalCount = (res.data && res.data.criticalCount) || 0;
        this.warningCount = (res.data && res.data.warningCount) || 0;
      } catch (e) {
        this.alerts = [];
      } finally {
        this.loadingAlerts = false;
      }
    },
    ruleLabel(code) {
      if (code === "R1A_ZERO_PROD") return "No production with sun";
      if (code === "R1B_OFFLINE") return "Inverter offline";
      if (code === "R1C_UNDERPROD") return "Under-production";
      if (code && code.startsWith("NATIVE_")) return "Solarman: " + code.replace("NATIVE_", "");
      return code || "–";
    },
    formatRelative(when) {
      if (!when) return "";
      const ms = Date.parse(when);
      if (!ms || isNaN(ms)) return String(when);
      const diffSec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
      if (diffSec < 60) return "just now";
      const min = Math.floor(diffSec / 60);
      if (min < 60) return `${min} min ago`;
      const hr = Math.floor(min / 60);
      if (hr < 24) return `${hr} h ago`;
      return `${Math.floor(hr / 24)} d ago`;
    },
    scoreFor(stationId) {
      return this.scoresByStation[stationId] ? this.scoresByStation[stationId].score : null;
    },
    scoreClassFor(stationId) {
      const s = this.scoreFor(stationId);
      if (s === null) return "";
      if (s >= 85) return "score-green";
      if (s >= 60) return "score-orange";
      return "score-red";
    },
    isDirty(s) {
      return (
        s._price !== s.electric_price_eur_per_kwh ||
        s._country !== (s.country_code || "") ||
        s._active !== s.is_active
      );
    },
    async saveStation(s) {
      try {
        const body = {
          electric_price_eur_per_kwh: s._price,
          country_code: (s._country || "").toUpperCase().slice(0, 2) || null,
          is_active: !!s._active,
        };
        const res = await axios.put(`/solar/admin/stations/${s.id}`, body);
        Object.assign(s, res.data, {
          _price: res.data.electric_price_eur_per_kwh,
          _country: res.data.country_code || "",
          _active: res.data.is_active,
        });
        this.$vs.notify({ title: "Solar admin", text: `Saved: ${s.name}`, color: "success" });
      } catch (e) {
        this.$vs.notify({ title: "Solar admin", text: e.response && e.response.data && e.response.data.message || e.message, color: "danger" });
      }
    },
  },
  mounted() {
    this.loadAll();
    this.loadAlerts();
  },
};
</script>

<style scoped>
.solar-score-pill {
  display: inline-block;
  min-width: 30px;
  height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  font-size: 11px;
  font-weight: 700;
  line-height: 22px;
  text-align: center;
  color: #fff;
}
.solar-score-pill.score-green  { background: #28C76F; }
.solar-score-pill.score-orange { background: #FF9F43; }
.solar-score-pill.score-red    { background: #EA5455; }

table th { font-weight: 600; color: #626262; font-size: 12px; }
table td { font-size: 13px; }

.alert-sev-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #fff;
}
.alert-sev-pill.sev-critical { background: #EA5455; }
.alert-sev-pill.sev-warning  { background: #FF9F43; }
.alert-sev-pill.sev-info     { background: #1E9FF2; }

.solar-alerts-table th { text-transform: uppercase; }
</style>
